import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Spinner, Center, Text, Flex, Button, useToast } from '@chakra-ui/react';
import { FaQrcode } from 'react-icons/fa';

import DeliveryList from '../components/DeliveryList';
import DeliverySummary from '../components/DeliverySummary';
import QrScanner from '../components/QrScanner';
import Header from '../components/common/Header';

import { getDeliveryGroups } from '../controller/api';

// 페이지네이션 응답이 비정상일 때 요청이 무한히 반복되는 것을 막기 위한 상한
const MAX_PAGES = 50;

const seoulDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
});

// 로케일이 붙이는 구분자 표기에 의존하지 않고 Asia/Seoul 기준 YYYY-MM-DD를 만든다
function getSeoulDate(date = new Date()) {
  const parts = {};
  for (const part of seoulDateFormatter.formatToParts(new Date(date))) {
    parts[part.type] = part.value;
  }
  return `${parts.year}-${parts.month}-${parts.day}`;
}

export default function DeliveryPage() {
  const driverName = localStorage.getItem('driverName');
  const deliveryDate = getSeoulDate();

  // 영수증 QR로 진입한 경우: ?delivery_id=<uuid>가 붙어 있으면
  // 목록 로딩 후 해당 배송묶음을 찾아 자동으로 펼친다.
  // 앱 안에서 QR을 스캔했을 때도 같은 파라미터를 세팅해 동일 경로를 탄다.
  const [searchParams, setSearchParams] = useSearchParams();
  const focusDeliveryId = searchParams.get('delivery_id');

  const [isScannerOpen, setIsScannerOpen] = useState(false);
  // 스캔할 때마다 증가시킨다. 같은 배송을 다시 스캔하면 URL이 그대로라
  // searchParams가 바뀌지 않아 목록이 반응하지 않는데, 이 값으로 구분한다.
  const [scanNonce, setScanNonce] = useState(0);
  // 목록 로딩이 끝난 뒤 "내 목록에 없는 QR"임을 알려주기 위해 보류해둔 id
  const [pendingScanId, setPendingScanId] = useState(null);
  const toast = useToast();

  const [deliveries, setDeliveries] = useState([]);
  // 서버가 알려준 완료 건수. 화면에 표시하는 값은 deliveries에서 직접 세고,
  // 이 값은 아직 불러오지 않은 페이지까지 포함한 하한으로만 사용한다.
  const [serverCompleteCount, setServerCompleteCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0); // API의 count 값을 저장할 상태 추가
  const [initialLoading, setInitialLoading] = useState(true); // 첫 페이지 로딩 상태
  const [loadingMore, setLoadingMore] = useState(false); // 추가 페이지 로딩 상태
  const [error, setError] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [noData, setNoData] = useState(false); // 데이터 없음 상태
  const [isRefreshing, setIsRefreshing] = useState(false);

  // 조회를 식별하는 토큰. 새 조회가 시작되면 값이 올라가고, 뒤늦게 도착한
  // 이전 조회의 응답은 화면을 덮어쓰지 않고 버려진다.
  const fetchRunIdRef = useRef(0);

  // 모든 페이지의 데이터를 가져오는 함수
  // isRefresh를 인자로 받는다. isRefreshing 상태를 읽으면 setIsRefreshing 직후
  // 호출되는 새로고침 경로에서 갱신 전 값(false)을 보게 되어 전체 로딩 화면으로 전환된다.
  const fetchAllOrders = useCallback(async (isRefresh = false) => {
    const runId = fetchRunIdRef.current + 1;
    fetchRunIdRef.current = runId;

    // 이 조회가 시작된 뒤 더 새로운 조회가 시작됐는지 확인한다
    const isStale = () => fetchRunIdRef.current !== runId;

    try {
      if (!isRefresh) {
        setInitialLoading(true);
      }
      setNoData(false); // 데이터 로딩 시작 시 noData 초기화
      setError(null); // 이전 시도의 실패 상태를 지워야 재시도가 화면에 반영된다
      setErrorMessage("");

      let allDeliveries = [];
      let nextUrl = null;
      let currentPage = 1;

      // 첫 페이지 데이터 로드
      const firstPageData = await getDeliveryGroups({
        page: currentPage,
        delivery_date: deliveryDate,
      });

      // 기다리는 사이 새 조회가 시작됐으면 이 응답은 버린다
      if (isStale()) {
        return;
      }

      // API에서 총 건수 저장
      if (firstPageData && firstPageData.count !== undefined) {
        setTotalCount(firstPageData.count);
        console.log(`총 배송 건수: ${firstPageData.count}개`);

        // 완료된 배송묶음 수도 API에서 직접 가져오기
        if (firstPageData.total_delivered_count !== undefined) {
          setServerCompleteCount(firstPageData.total_delivered_count);
          console.log(`완료된 배송 건수: ${firstPageData.total_delivered_count}개`);
        }

        // 데이터가 없는 경우 처리
        if (firstPageData.count === 0) {
          setNoData(true);
          setInitialLoading(false);
          setIsRefreshing(false);
          return; // 더 이상 진행하지 않음
        }
      }

      // 첫 페이지 결과 설정
      if (firstPageData.results && firstPageData.results.length > 0) {
        allDeliveries = [...firstPageData.results];
        setDeliveries(allDeliveries); // 첫 페이지 데이터 즉시 표시
      } else {
        // 결과는 있지만 비어있는 경우
        setNoData(true);
      }

      setInitialLoading(false); // 초기 로딩 완료

      // 다음 페이지 확인
      nextUrl = firstPageData.next;
      currentPage++;

      // 추가 페이지가 있으면 백그라운드에서 계속 로드
      if (nextUrl) {
        setLoadingMore(true); // 추가 로딩 시작

        do {
          // 다음 페이지 데이터 로드
          const data = await getDeliveryGroups({
            page: currentPage,
            delivery_date: deliveryDate,
          });

          // 새 조회가 시작됐으면 남은 페이지를 더 받지 않고 중단한다
          if (isStale()) {
            return;
          }

          // 빈 페이지가 오면 next 값과 무관하게 중단 (더 받을 데이터가 없음)
          if (!data.results || data.results.length === 0) {
            break;
          }

          // 결과 누적 및 UI 업데이트
          allDeliveries = [...allDeliveries, ...data.results];
          setDeliveries(allDeliveries); // 데이터가 추가될 때마다 UI 업데이트

          // 다음 페이지 확인
          nextUrl = data.next;

          // 페이지 번호 증가
          if (nextUrl) {
            currentPage++;
          }

          // 서버가 next를 계속 반환하는 경우에도 요청이 무한히 늘어나지 않도록 상한을 둔다
          if (currentPage > MAX_PAGES) {
            console.warn(`페이지 상한(${MAX_PAGES})에 도달하여 조회를 중단합니다. 일부 배송 건이 누락될 수 있습니다.`);
            break;
          }

        } while (nextUrl); // 다음 페이지가 없을 때까지 반복

        console.log(`총 ${allDeliveries.length}개 배송 정보 로드 완료`);
        setLoadingMore(false); // 추가 로딩 완료
      } else {
        // 단일 페이지만 있는 경우
        setLoadingMore(false);
      }

    } catch (error) {
      console.error('데이터 가져오기 실패:', error);

      // 이미 새 조회가 진행 중이면 지난 조회의 실패로 화면을 덮지 않는다
      if (isStale()) {
        return;
      }

      setError(error);
      setErrorMessage(error?.response?.data?.message || '데이터를 가져오는 중 오류가 발생했습니다.');
      setInitialLoading(false);
      setLoadingMore(false);
      setIsRefreshing(false);
    }
  }, [deliveryDate]);

  useEffect(() => {
    fetchAllOrders();
  }, [fetchAllOrders]);

  const handleRefresh = async () => {
    // 진행 중인 새로고침이 있으면 무시한다. 중복 실행되면 각 호출이 자기 누적
    // 배열로 setDeliveries를 호출해 목록이 뒤섞인다.
    if (isRefreshing) {
      return;
    }
    setIsRefreshing(true);

    try {
      // 모든 데이터 다시 가져오기
      await fetchAllOrders(true);
      console.log('새로고침 완료');
    } catch (error) {
      console.error('새로고침 중 오류 발생:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // 오류 화면에는 유지할 목록이 없으므로 새로고침이 아닌 초기 로딩으로 다시 조회한다.
  // isRefresh=true로 부르면 error만 지워진 채 "배송 정보가 없습니다" 화면이 잠깐 노출된다.
  const handleRetry = () => {
    fetchAllOrders(false);
  };

  // 앱 안 QR 스캔 결과 처리.
  // 목록에 있으면 delivery_id 파라미터를 세팅해 QR 링크로 들어온 것과 똑같이
  // 자동 펼침 경로를 태운다. 아직 뒤 페이지를 불러오는 중이면 없다고 단정할 수
  // 없으므로, 파라미터만 세팅해두고 도착하는 시점에 펼쳐지도록 맡긴다.
  const notifyUnknownQr = useCallback(() => {
    toast({
      title: '오늘 배송 목록에 없는 QR입니다',
      description: '다른 날짜의 영수증이거나 다른 기사님께 배정된 배송일 수 있습니다.',
      status: 'warning',
      duration: 4000,
      isClosable: true,
    });
  }, [toast]);

  const handleQrDetect = useCallback((deliveryId) => {
    setIsScannerOpen(false);

    const found = deliveries.some(delivery => delivery.delivery_id === deliveryId);

    if (!found) {
      if (loadingMore) {
        // 아직 뒤 페이지를 받는 중이라 없다고 단정할 수 없다.
        // 로딩이 끝난 뒤 다시 판단하도록 보류해둔다.
        setPendingScanId(deliveryId);
      } else {
        notifyUnknownQr();
        return;
      }
    } else {
      setPendingScanId(null);
    }

    // replace로 넣어야 스캔할 때마다 히스토리가 쌓이지 않는다.
    // (쌓이면 뒤로가기가 이전에 스캔한 배송을 다시 열어버린다)
    setSearchParams({ delivery_id: deliveryId }, { replace: true });
    setScanNonce(prev => prev + 1);
  }, [deliveries, loadingMore, notifyUnknownQr, setSearchParams]);

  // 보류해둔 스캔 결과 판정. 로딩이 끝났는데도 목록에 없으면 그때 알린다.
  useEffect(() => {
    if (!pendingScanId || loadingMore) {
      return;
    }

    if (!deliveries.some(delivery => delivery.delivery_id === pendingScanId)) {
      notifyUnknownQr();
    }
    setPendingScanId(null);
  }, [pendingScanId, loadingMore, deliveries, notifyUnknownQr]);

  const handleDelivered = (delivery_id) => {
    setDeliveries(prev =>
      prev.map(delivery =>
        delivery.delivery_id === delivery_id
          ? { ...delivery, status: '04', status_in_word: '배송완료' }
          : delivery
      )
    );
  };

  const handleDeliveryFailed = (delivery_id) => {
    setDeliveries(prev =>
      prev.map(delivery =>
        delivery.delivery_id === delivery_id
          ? { ...delivery, status: '03', status_in_word: '배송중' }
          : delivery
      )
    );
  };

  // 첫 로딩 중일 때 전체 화면 로딩 표시
  if (initialLoading) {
    return (
      <Box>
        <Header />
        <Center h="50vh" flexDirection="column">
          <Spinner size="xl" color="orange.500" mb={4} />
          <Text>배송 정보를 불러오는 중입니다...</Text>
        </Center>
      </Box>
    );
  }

  // 오류 발생 시
  if (error) {
    return (
      <Box>
        <Header />
        <Center h="50vh" flexDirection="column" gap={4} p={4}>
          <Text color="red.500" textAlign="center">{errorMessage}</Text>
          <Button colorScheme="orange" onClick={handleRetry}>
            다시 시도
          </Button>
        </Center>
      </Box>
    );
  }

  // 데이터가 없는 경우
  if (noData || deliveries.length === 0) {
    return (
      <Box>
        <Header />
        <DeliverySummary
          userName={driverName}
          completed={0}
          total={0}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
        />
        <Center h="40vh" flexDirection="column" p={4}>
          <Text fontSize="lg" color="gray.500">
            금일 배정된 배송 정보가 없습니다.
          </Text>
        </Center>

        <QrScanButton onClick={() => setIsScannerOpen(true)} />
        <QrScanner isOpen={isScannerOpen} onClose={() => setIsScannerOpen(false)} onDetect={handleQrDetect} />
      </Box>
    );
  }

  // 불러온 목록에서 직접 센 값을 기준으로 삼는다. 별도 카운터를 증감시키면
  // 서버가 알려준 값과 로컬 증가분이 섞여 첫 완료 처리 때 숫자가 붕괴한다.
  const loadedCompleteCount = deliveries.filter(item => item.status_in_word === '배송완료').length;
  const completedDeliveries = Math.max(serverCompleteCount, loadedCompleteCount);
  const totalDeliveries = totalCount > 0 ? totalCount : deliveries.length;

  return (
    <Box>
      <Header />
      <DeliverySummary
        userName={driverName}
        completed={completedDeliveries}
        total={totalDeliveries}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
      />

      <DeliveryList
        deliveries={deliveries}
        deliveryDate={deliveryDate}
        isLoadingMore={loadingMore}
        onDelivered={handleDelivered}
        onDeliveryFailed={handleDeliveryFailed}
        focusDeliveryId={focusDeliveryId}
        focusNonce={scanNonce}
      />

      {/* 추가 데이터 로딩 중 표시 */}
      {loadingMore && (
        <Flex justifyContent="center" py={4}>
          <Spinner size="md" color="orange.500" mr={2} />
          <Text>추가 배송 정보를 불러오는 중...</Text>
        </Flex>
      )}

      {/* 스티키 버튼이 마지막 카드를 가리지 않도록 아래 여백을 둔다 */}
      <Box height="88px" />

      <QrScanButton onClick={() => setIsScannerOpen(true)} />
      <QrScanner isOpen={isScannerOpen} onClose={() => setIsScannerOpen(false)} onDetect={handleQrDetect} />
    </Box>
  );
}

// 화면 하단에 고정되는 QR 스캔 버튼.
// 앱 컨테이너가 maxW 500px로 가운데 정렬돼 있어 버튼도 같은 폭에 맞춘다.
function QrScanButton({ onClick }) {
  return (
    <Box
      position="fixed"
      bottom="0"
      left="50%"
      transform="translateX(-50%)"
      width="100%"
      maxW="500px"
      px={4}
      pt={6}
      pb={4}
      zIndex={10}
      // 목록이 버튼 뒤로 스크롤될 때 글자가 겹쳐 보이지 않도록 위쪽을 흐린다
      bgGradient="linear(to-t, white 65%, rgba(255,255,255,0))"
      pointerEvents="none"
    >
      <Button
        leftIcon={<FaQrcode />}
        colorScheme="orange"
        size="lg"
        width="100%"
        onClick={onClick}
        pointerEvents="auto"
        boxShadow="lg"
      >
        영수증 QR 스캔
      </Button>
    </Box>
  );
}